import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projectsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, FolderKanban, Database, BarChart3, ArrowRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', drug_name: '', drug_class: 'small_molecule', therapeutic_area: '', regulatory_target: 'fda', description: '' });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = async () => {
    try {
      const res = await projectsAPI.list();
      setProjects(res.data.projects || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const res = await projectsAPI.create(form);
      toast.success('Project created');
      setDialogOpen(false);
      setForm({ name: '', drug_name: '', therapeutic_area: '', regulatory_target: 'fda', description: '' });
      navigate(`/app/project/${res.data.id}`);
    } catch (e) {
      toast.error('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project?')) return;
    try {
      await projectsAPI.delete(id);
      setProjects(p => p.filter(x => x.id !== id));
      toast.success('Project deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = projects.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.drug_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Projects</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm active:scale-[0.98]" data-testid="create-project-button">
              <Plus className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">New </span>Project
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Create Project</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Phase 1 PK Study" data-testid="project-name-input" className="h-11 rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Drug Name</Label>
                  <Input value={form.drug_name} onChange={(e) => setForm({...form, drug_name: e.target.value})} placeholder="Compound-X" className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Drug Class</Label>
                  <Select value={form.drug_class} onValueChange={(v) => setForm({...form, drug_class: v})}>
                    <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small_molecule">Small Molecule</SelectItem>
                      <SelectItem value="mab">Monoclonal Antibody</SelectItem>
                      <SelectItem value="peptide">Peptide</SelectItem>
                      <SelectItem value="adc">ADC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Therapeutic Area</Label>
                  <Select value={form.therapeutic_area || 'oncology'} onValueChange={(v) => setForm({...form, therapeutic_area: v})}>
                    <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oncology">Oncology</SelectItem>
                      <SelectItem value="immunology">Immunology</SelectItem>
                      <SelectItem value="neurology">Neurology</SelectItem>
                      <SelectItem value="cardiology">Cardiology</SelectItem>
                      <SelectItem value="infectious_disease">Infectious Disease</SelectItem>
                      <SelectItem value="metabolic">Metabolic</SelectItem>
                      <SelectItem value="rare_disease">Rare Disease</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Regulatory Target</Label>
                  <Select value={form.regulatory_target} onValueChange={(v) => setForm({...form, regulatory_target: v})}>
                    <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fda">FDA</SelectItem>
                      <SelectItem value="ema">EMA</SelectItem>
                      <SelectItem value="pmda">PMDA</SelectItem>
                      <SelectItem value="nmpa">NMPA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCreate} disabled={creating || !form.name.trim()} className="rounded-full h-10 px-5 text-sm" data-testid="confirm-create-project">
                {creating ? 'Creating...' : 'Create Project'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative mb-4 sm:mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects..."
          data-testid="project-search-input"
          className="pl-9 h-11 rounded-xl"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center py-16">
            <FolderKanban className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">{search ? 'No matching projects' : 'No projects yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((project, i) => (
            <motion.div key={project.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Card className="card-hover cursor-pointer" onClick={() => navigate(`/app/project/${project.id}`)} data-testid={`project-item-${i}`}>
                <CardContent className="py-4 px-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>{project.name}</h3>
                      <div className="flex items-center gap-3 mt-1.5">
                        {project.drug_name && <span className="text-xs font-mono text-muted-foreground">{project.drug_name}</span>}
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Database className="w-3 h-3" /> {project.dataset_count || 0}</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {project.session_count || 0}</span>
                        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${project.regulatory_target === 'fda' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'}`}>
                          {project.regulatory_target?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => handleDelete(e, project.id)}>
                        <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                      </Button>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
